import { useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import ProjectManagement from "../component/ProjectManagementData";
import theme from "../theme";
import "../styles/TitleStyle.css";

type WorkspaceKey =
  | "projects"
  | "activities"
  | "tasks"
  | "issues"
  | "meetings"
  | "documents";

type WorkspaceTab = {
  key: WorkspaceKey;
  label: string;
  actionLabel: string;
  icon: React.ReactElement;
  description: string;
};

type DraftRecord = {
  id: string;
  workspace: WorkspaceKey;
  title: string;
  projectName: string;
  owner: string;
  status: string;
  date: string;
  detail: string;
};

type RegistrationForm = Omit<DraftRecord, "id" | "workspace">;

const pageColors = {
  pageBg: "#2D2C3A",
  panel: "#3A414C",
  panelDark: "#1F2530",
  panelDeep: "#1F1D30",
  accent: "#C5E763",
  text: "#FFFFFF",
  textSoft: "rgba(232,238,245,0.78)",
  textMuted: "#9FB0C0",
  border: "rgba(217,217,217,0.12)",
};

const workspaceTabs: WorkspaceTab[] = [
  {
    key: "projects",
    label: "案件一覧",
    actionLabel: "案件を登録",
    icon: <FolderOutlinedIcon />,
    description: "案件の現在状況、担当者、次の対応を確認します。",
  },
  {
    key: "activities",
    label: "日報・活動記録",
    actionLabel: "日報を登録",
    icon: <EventNoteOutlinedIcon />,
    description: "案件を横断して日報、訪問、作業、顧客対応を確認します。",
  },
  {
    key: "tasks",
    label: "タスク",
    actionLabel: "タスクを追加",
    icon: <TaskAltOutlinedIcon />,
    description: "未完了の作業、担当者、期限を横断して確認します。",
  },
  {
    key: "issues",
    label: "課題・不具合",
    actionLabel: "課題を登録",
    icon: <WarningAmberOutlinedIcon />,
    description: "製造上の問題とIT上の障害・課題をまとめて管理します。",
  },
  {
    key: "meetings",
    label: "打合せ・決定事項",
    actionLabel: "打合せ記録を追加",
    icon: <GroupsOutlinedIcon />,
    description: "会議内容、決定事項、保留事項を案件ごとに残します。",
  },
  {
    key: "documents",
    label: "資料",
    actionLabel: "資料を追加",
    icon: <DescriptionOutlinedIcon />,
    description: "仕様書、図面、写真、議事録などを案件単位で管理します。",
  },
];

const initialForm: RegistrationForm = {
  title: "",
  projectName: "",
  owner: "",
  status: "未着手",
  date: new Date().toISOString().slice(0, 10),
  detail: "",
};

const SDMSExiCustomers = () => {
  const { isAuthenticated } = useAuth0();

  const [tabIndex, setTabIndex] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<RegistrationForm>(initialForm);
  const [drafts, setDrafts] = useState<DraftRecord[]>([]);
  const [notice, setNotice] = useState<string | null>(null);

  const currentTab = workspaceTabs[tabIndex];

  const filteredDrafts = useMemo(() => {
    const keyword = searchText.normalize("NFKC").toLowerCase().trim();

    return drafts.filter((record) => {
      if (record.workspace !== currentTab.key) return false;
      if (!keyword) return true;

      return [
        record.title,
        record.projectName,
        record.owner,
        record.status,
        record.detail,
      ]
        .join(" ")
        .normalize("NFKC")
        .toLowerCase()
        .includes(keyword);
    });
  }, [currentTab.key, drafts, searchText]);

  const openRegistrationDialog = () => {
    setForm(initialForm);
    setDialogOpen(true);
  };

  const saveDraft = () => {
    if (!form.title.trim()) {
      setNotice("件名を入力してください。");
      return;
    }

    const newDraft: DraftRecord = {
      id: crypto.randomUUID(),
      workspace: currentTab.key,
      ...form,
      title: form.title.trim(),
    };

    setDrafts((current) => [newDraft, ...current]);
    setDialogOpen(false);
    setNotice(
      `${currentTab.actionLabel}の入力内容を画面内ドラフトとして追加しました。API保存処理へ接続してください。`,
    );
  };

  const deleteDraft = (id: string) => {
    setDrafts((current) => current.filter((record) => record.id !== id));
  };

  if (!isAuthenticated) return null;

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          height: "100dvh",
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          backgroundColor: pageColors.pageBg,
          color: pageColors.text,
        }}
      >
        <Toolbar sx={{ flexShrink: 0 }} />

        <Paper
          elevation={0}
          square
          sx={{
            flexShrink: 0,
            px: { xs: 1.5, md: 2.5 },
            pt: 1.2,
            backgroundColor: pageColors.panelDeep,
            borderBottom: `1px solid ${pageColors.border}`,
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <AssignmentOutlinedIcon sx={{ color: pageColors.accent }} />
                <Typography variant="h5" fontWeight={900}>
                  案件・活動管理
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{ mt: 0.35, color: pageColors.textSoft }}
              >
                製造・IT案件の経緯、対応状況、日報、課題、決定事項を管理します。
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                size="small"
                placeholder={`${currentTab.label}を検索`}
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon sx={{ color: pageColors.textMuted }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: { xs: "100%", sm: 260 },
                  "& .MuiOutlinedInput-root": {
                    color: pageColors.text,
                    backgroundColor: pageColors.panelDark,
                    borderRadius: 2,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: pageColors.border,
                  },
                }}
              />

              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={openRegistrationDialog}
                sx={{
                  color: "#20251B",
                  backgroundColor: pageColors.accent,
                  fontWeight: 900,
                  whiteSpace: "nowrap",
                  "&:hover": { backgroundColor: "#D3EF7D" },
                }}
              >
                {currentTab.actionLabel}
              </Button>
            </Stack>
          </Stack>

          <Tabs
            value={tabIndex}
            onChange={(_, newValue: number) => {
              setTabIndex(newValue);
              setSearchText("");
              setNotice(null);
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              mt: 1.4,
              minHeight: 44,
              "& .MuiTabs-indicator": {
                height: 3,
                backgroundColor: pageColors.accent,
              },
              "& .MuiTab-root": {
                minHeight: 44,
                color: pageColors.textMuted,
                fontWeight: 700,
                textTransform: "none",
              },
              "& .MuiTab-root.Mui-selected": {
                color: pageColors.accent,
                fontWeight: 900,
              },
            }}
          >
            {workspaceTabs.map((tab) => (
              <Tab
                key={tab.key}
                icon={tab.icon}
                iconPosition="start"
                label={tab.label}
              />
            ))}
          </Tabs>
        </Paper>

        {notice && (
          <Alert
            severity={notice.includes("入力してください") ? "warning" : "info"}
            onClose={() => setNotice(null)}
            sx={{ mx: 2, mt: 1.5, flexShrink: 0 }}
          >
            {notice}
          </Alert>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            p: { xs: 1, md: 1.5 },
          }}
        >
          {currentTab.key === "projects" ? (
            <Stack sx={{ height: "100%", minHeight: 0 }} spacing={1}>
              {filteredDrafts.length > 0 && (
                <DraftList
                  records={filteredDrafts}
                  onDelete={deleteDraft}
                  title="案件登録ドラフト"
                />
              )}
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <ProjectManagement embedded />
              </Box>
            </Stack>
          ) : (
            <WorkspacePanel
              tab={currentTab}
              records={filteredDrafts}
              onlyMine={onlyMine}
              onOnlyMineChange={setOnlyMine}
              onRefresh={() =>
                setNotice(
                  `${currentTab.label}の再取得処理をAPIへ接続してください。`,
                )
              }
              onCreate={openRegistrationDialog}
              onDelete={deleteDraft}
            />
          )}
        </Box>

        <RegistrationDialog
          open={dialogOpen}
          tab={currentTab}
          form={form}
          onChange={setForm}
          onClose={() => setDialogOpen(false)}
          onSave={saveDraft}
        />
      </Box>
    </ThemeProvider>
  );
};

type WorkspacePanelProps = {
  tab: WorkspaceTab;
  records: DraftRecord[];
  onlyMine: boolean;
  onOnlyMineChange: (value: boolean) => void;
  onRefresh: () => void;
  onCreate: () => void;
  onDelete: (id: string) => void;
};

const WorkspacePanel = ({
  tab,
  records,
  onlyMine,
  onOnlyMineChange,
  onRefresh,
  onCreate,
  onDelete,
}: WorkspacePanelProps) => {
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: 3,
        backgroundColor: pageColors.panel,
        border: `1px solid ${pageColors.border}`,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        spacing={1}
        sx={{
          px: 2,
          py: 1.3,
          flexShrink: 0,
          borderBottom: `1px solid ${pageColors.border}`,
          backgroundColor: pageColors.panelDeep,
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ color: pageColors.accent }}>{tab.icon}</Box>
            <Typography fontWeight={900}>{tab.label}</Typography>
            <Chip
              size="small"
              label={`${records.length}件`}
              sx={{
                color: pageColors.accent,
                backgroundColor: "rgba(197,231,99,0.12)",
                fontWeight: 800,
              }}
            />
          </Stack>
          <Typography
            variant="body2"
            sx={{ mt: 0.35, color: pageColors.textMuted }}
          >
            {tab.description}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={onlyMine}
                onChange={(event) => onOnlyMineChange(event.target.checked)}
              />
            }
            label="自分の担当のみ"
            sx={{
              m: 0,
              color: pageColors.textSoft,
              "& .MuiFormControlLabel-label": { fontSize: 13 },
            }}
          />

          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshRoundedIcon />}
            onClick={onRefresh}
            sx={{ color: pageColors.textSoft, borderColor: pageColors.border }}
          >
            再取得
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 2 }}>
        {records.length > 0 ? (
          <DraftList
            records={records}
            onDelete={onDelete}
            title="画面内ドラフト"
          />
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1.3}
            sx={{ minHeight: 280, textAlign: "center" }}
          >
            <Box
              sx={{
                color: pageColors.textMuted,
                "& .MuiSvgIcon-root": { fontSize: 48 },
              }}
            >
              {tab.icon}
            </Box>
            <Typography variant="h6" fontWeight={900}>
              {tab.label}はまだ登録されていません
            </Typography>
            <Typography
              sx={{
                maxWidth: 560,
                color: pageColors.textMuted,
                lineHeight: 1.8,
              }}
            >
              案件を指定して記録を追加し、一覧取得・編集・完了処理を各APIへ接続する構成です。
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddRoundedIcon />}
              onClick={onCreate}
              sx={{
                color: "#20251B",
                backgroundColor: pageColors.accent,
                fontWeight: 900,
              }}
            >
              {tab.actionLabel}
            </Button>
          </Stack>
        )}
      </Box>
    </Paper>
  );
};

type DraftListProps = {
  title: string;
  records: DraftRecord[];
  onDelete: (id: string) => void;
};

const DraftList = ({ title, records, onDelete }: DraftListProps) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.2,
        borderRadius: 2,
        backgroundColor: pageColors.panelDark,
        border: `1px solid ${pageColors.border}`,
      }}
    >
      <Typography
        variant="caption"
        fontWeight={900}
        sx={{ color: pageColors.accent }}
      >
        {title}
      </Typography>

      <Stack spacing={0.8} sx={{ mt: 0.8 }}>
        {records.map((record) => (
          <Stack
            key={record.id}
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            spacing={1}
            sx={{
              p: 1,
              borderRadius: 1.5,
              backgroundColor: "rgba(255,255,255,0.04)",
              border: `1px solid ${pageColors.border}`,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                spacing={0.7}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Typography fontWeight={900}>{record.title}</Typography>
                <Chip size="small" label={record.status} />
              </Stack>
              <Typography
                variant="body2"
                sx={{ mt: 0.3, color: pageColors.textMuted }}
              >
                {record.projectName || "案件未指定"} /{" "}
                {record.owner || "担当未設定"} / {record.date}
              </Typography>
            </Box>

            <Button
              size="small"
              color="error"
              onClick={() => onDelete(record.id)}
            >
              削除
            </Button>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

type RegistrationDialogProps = {
  open: boolean;
  tab: WorkspaceTab;
  form: RegistrationForm;
  onChange: (form: RegistrationForm) => void;
  onClose: () => void;
  onSave: () => void;
};

const RegistrationDialog = ({
  open,
  tab,
  form,
  onChange,
  onClose,
  onSave,
}: RegistrationDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          color: pageColors.text,
          backgroundColor: pageColors.pageBg,
          border: `1px solid ${pageColors.border}`,
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle fontWeight={900}>{tab.actionLabel}</DialogTitle>

      <DialogContent dividers sx={{ borderColor: pageColors.border }}>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <TextField
            autoFocus
            required
            label={tab.key === "projects" ? "案件名" : "件名"}
            value={form.title}
            onChange={(event) =>
              onChange({ ...form, title: event.target.value })
            }
          />

          {tab.key !== "projects" && (
            <TextField
              label="対象案件"
              value={form.projectName}
              onChange={(event) =>
                onChange({ ...form, projectName: event.target.value })
              }
            />
          )}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              fullWidth
              label="担当者"
              value={form.owner}
              onChange={(event) =>
                onChange({ ...form, owner: event.target.value })
              }
            />
            <TextField
              fullWidth
              select
              label="状態"
              value={form.status}
              onChange={(event) =>
                onChange({ ...form, status: event.target.value })
              }
            >
              <MenuItem value="未着手">未着手</MenuItem>
              <MenuItem value="対応中">対応中</MenuItem>
              <MenuItem value="確認待ち">確認待ち</MenuItem>
              <MenuItem value="完了">完了</MenuItem>
              <MenuItem value="保留">保留</MenuItem>
            </TextField>
          </Stack>

          <TextField
            type="date"
            label={tab.key === "tasks" ? "期限" : "日付"}
            value={form.date}
            onChange={(event) =>
              onChange({ ...form, date: event.target.value })
            }
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            multiline
            minRows={4}
            label="内容"
            value={form.detail}
            onChange={(event) =>
              onChange({ ...form, detail: event.target.value })
            }
          />

          <Alert severity="info">
            現在は画面内ドラフトへの追加です。実運用時は登録APIへ接続してください。
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} sx={{ color: pageColors.textSoft }}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={onSave}
          sx={{
            color: "#20251B",
            backgroundColor: pageColors.accent,
            fontWeight: 900,
          }}
        >
          ドラフトへ追加
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SDMSExiCustomers;
